import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from '../components/common/toast';
import { returnService, productService } from '../services/api';
import { Table, Badge, EditIcon, DeleteIcon, FormPopup, DeletePopUp, ReturnDetailsModal } from '../components/common';
import SkeletonTable from '../components/SkeletonTable';

const getStatusVariant = (status) => {
  return 'success'; // Only RECEIVED - from backend
};

// Status is read-only from backend; no status change allowed
function ReturnStatusCell({ status, getStatusVariant }) {
  if (!status) return '—';
  return (
    <Badge variant={getStatusVariant(status)}>
      <span>{status.replace(/_/g, ' ')}</span>
    </Badge>
  );
}

export function ReturnManagement() {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [outwardInvoices, setOutwardInvoices] = useState([]);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [batchSelections, setBatchSelections] = useState({});
  // OLD
  // const [receiveQty, setReceiveQty] = useState('');
  // NEW: store qty per batch
  const [receiveQtyMap, setReceiveQtyMap] = useState({});
  // CURRENT - WRONG (derived from batch inputs, not user input)
  // const [totalReceivedQty, setTotalReceivedQty] = useState(0);
  // OLD - CORRECT: user-entered received qty (can be <= dispatched)
  const [totalReceivedQty, setTotalReceivedQty] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    productId: '',
    invoiceQuantity: 0,
    receivedQuantity: 0,
    returnReason: 'DEFECTIVE',
    vehicleNumber: '',
    location: '',
    batches: [],
  });
  const [currentBatch, setCurrentBatch] = useState({
    batchNumber: '',
    manufacturingDate: '',
    expiryDate: '',
    quantity: 0,
  });
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const {
    data: returnTransactionsResponse,
    isLoading,
    isFetching,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: ['returnTransactions', page, limit],
    queryFn: async () => {
      const params = { page, limit };
      return returnService.getReturnTransactions(params);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
  const loading = isLoading || isFetching;

  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState(null);
  const viewRowRef = useRef(null);

  // Normalize search once for smart, case-insensitive filtering (like Product Master)
  const normalizedSearch = useMemo(
    () => (searchTerm ? searchTerm.trim().toLowerCase() : ''),
    [searchTerm]
  );

  // Frontend smart search over common visible fields (do not rely on backend search matching)
  const filteredTransactions = useMemo(() => {
    if (!normalizedSearch) return transactions;
    const term = normalizedSearch;
    return (transactions || []).filter((t) => {
      const txId = String(t.transactionId ?? t._id ?? t.id ?? '').toLowerCase();
      const invoice = String(t.invoiceNumber ?? '').toLowerCase();
      const reason = String(t.returnReason ?? '').toLowerCase();
      const vehicle = String(t.vehicleNumber ?? '').toLowerCase();
      const status = String(t.status ?? '').toLowerCase();
      const location = String(t.location ?? '').toLowerCase();

      const productObj = t.productId && typeof t.productId === 'object' ? t.productId : null;
      const sku = String(productObj?.sku ?? t.productSku ?? t.productCode ?? t.productId ?? '').toLowerCase();
      const nameDesc = String(productObj?.skuName ?? productObj?.description ?? t.productDescription ?? '').toLowerCase();

      return (
        txId.includes(term) ||
        invoice.includes(term) ||
        sku.includes(term) ||
        nameDesc.includes(term) ||
        reason.includes(term) ||
        vehicle.includes(term) ||
        status.includes(term) ||
        location.includes(term)
      );
    });
  }, [transactions, normalizedSearch]);

  const selectedFlatRows = useMemo(() => {
    const invoiceProducts = getInvoiceProducts();
    const productNameById = (invoiceProducts || []).reduce((acc, it) => {
      const pid = String(it.productId);
      const sku = it.product?.sku || pid;
      const name = it.product?.description || '';
      acc[pid] = name ? `${sku} / ${name}` : sku;
      return acc;
    }, {});

    return (selectedProducts || []).flatMap((product) =>
      (product.batchDetails || []).map((batch) => ({
        productId: product.productId,
        productName: productNameById[String(product.productId)] || String(product.productId),
        dispatchQuantity: product.dispatchQuantity,
        invoiceQuantity: product.invoiceQuantity ?? product.dispatchQuantity,
        receivedQuantity: product.receivedQuantity || 0,
        batchNumber: batch.batchNumber,
        mfgDate: batch.mfgDate ?? batch.manufacturingDate ?? null,
        expiryDate: batch.expiryDate ?? null,
        availableQuantity: batch.availableQuantity ?? batch.quantity ?? 0,
      }))
    );
  }, [selectedProducts, products, outwardInvoices, selectedInvoiceData]);

  const totalBatchQty = useMemo(
    () =>
      Object.values(receiveQtyMap).reduce(
        (sum, val) => sum + Number(val || 0),
        0
      ),
    [receiveQtyMap]
  );

  const totalReceivedQtyNumber = useMemo(
    () => Number(totalReceivedQty || 0),
    [totalReceivedQty]
  );

  const hasValidBatchQty = useMemo(
    () => Object.values(receiveQtyMap).some((val) => Number(val || 0) > 0),
    [receiveQtyMap]
  );

  const isReturnQtyInvalid = useMemo(() => {
    if (selectedProducts.length === 0) return true;
    if (totalReceivedQtyNumber <= 0) return true;
    if (totalBatchQty <= 0) return true;
    if (!hasValidBatchQty) return true;
    return false;
  }, [
    selectedProducts.length,
    totalReceivedQtyNumber,
    totalBatchQty,
    hasValidBatchQty,
  ]);

  const returnColumns = [
    { key: 'transactionId', label: 'Transaction ID' },
    { key: 'invoiceNumber', label: 'DO Number' },
    {
      key: 'productId',
      label: 'Product',
      render: (val) => (val?.sku ?? '—'),
    },
    { key: 'receivedQuantity', label: 'Received Qty', align: 'right' },
    { key: 'returnReason', label: 'Return Reason' },
    {
      key: 'vehicleNumber',
      label: 'Vehicle',
      render: (v) => v || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <ReturnStatusCell status={val} getStatusVariant={getStatusVariant} />
      ),
    },
  ];

  useEffect(() => {
    fetchProducts();
    fetchOutwardInvoices();
  }, []);

  useEffect(() => {
    setTransactions(returnTransactionsResponse?.data || []);
  }, [returnTransactionsResponse]);

  // CURRENT - WRONG: auto syncing received qty from batch inputs
  // useEffect(() => {
  //   const total = Object.values(receiveQtyMap).reduce(
  //     (sum, val) => sum + Number(val || 0),
  //     0
  //   );
  //   setTotalReceivedQty(total);
  // }, [receiveQtyMap]);

  // Debounce search
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     fetchTransactions();
  //   }, 300);
  //   return () => clearTimeout(timer);
  // }, [searchTerm]);
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);
  // Also watch for outwardInvoices loading - if invoiceNumber is already set, update vehicle number
  useEffect(() => {
    if (formData.invoiceNumber && outwardInvoices.length > 0 && showForm) {
      const invoice = outwardInvoices.find(
        (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === formData.invoiceNumber
      );
      
      if (invoice && invoice.vehicleNumber) {
        // Update vehicle number if it's different or not set
        setFormData((prev) => {
          if (prev.vehicleNumber !== invoice.vehicleNumber) {
            return {
              ...prev,
              vehicleNumber: invoice.vehicleNumber,
            };
          }
          return prev;
        });
      }
    }
  }, [outwardInvoices.length, formData.invoiceNumber, showForm]);

  // Watch for invoice number changes and auto-populate invoice date and vehicle number
  useEffect(() => {
    if (formData.invoiceNumber && outwardInvoices.length > 0) {
      const invoice = outwardInvoices.find(
        (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === formData.invoiceNumber
      );
      
      if (invoice) {
        // Set selected invoice data
        setSelectedInvoiceData(invoice);
        // Reset selected products when invoice changes
        setSelectedProducts([]);
        setBatchSelections({});
        // NEW: keep batch inputs empty initially (no auto sync)
        setReceiveQtyMap({});
        
        // Prepare update data
        const updateData = {};
        
        // Parse and set invoice date
        if (invoice.invoiceDate) {
          try {
            let dateObj;
            const invoiceDateValue = invoice.invoiceDate;
            
            // Handle different date formats
            if (typeof invoiceDateValue === 'string') {
              dateObj = new Date(invoiceDateValue);
            } else if (invoiceDateValue instanceof Date) {
              dateObj = invoiceDateValue;
            } else if (invoiceDateValue && typeof invoiceDateValue === 'object' && invoiceDateValue.$date) {
              // MongoDB date format
              dateObj = new Date(invoiceDateValue.$date);
            } else {
              dateObj = new Date(invoiceDateValue);
            }
            
            if (!isNaN(dateObj.getTime())) {
              const formattedDate = dateObj.toISOString().split('T')[0];
              updateData.invoiceDate = formattedDate;
            } else {
              console.warn('Invalid date format:', invoiceDateValue);
            }
          } catch (error) {
            console.error('Error parsing invoice date:', error, invoice.invoiceDate);
          }
        }
        
        // Always set vehicle number (don't depend on date parsing)
        updateData.vehicleNumber = invoice.vehicleNumber || '';
        
        // Update form data - always update vehicle number when invoice changes
        setFormData((prev) => {
          const newData = { ...prev };
          let hasChanges = false;
          
          // Update invoice date if different
          if (updateData.invoiceDate && prev.invoiceDate !== updateData.invoiceDate) {
            newData.invoiceDate = updateData.invoiceDate;
            hasChanges = true;
          }
          
          // Always update vehicle number when invoice changes (even if same value, to ensure it's set)
          if (prev.vehicleNumber !== updateData.vehicleNumber) {
            newData.vehicleNumber = updateData.vehicleNumber;
            hasChanges = true;
          }
          
          return hasChanges ? newData : prev;
        });
      }
    }
  }, [formData.invoiceNumber, outwardInvoices]);

  const fetchProducts = async () => {
    try {
      const result = await productService.getProducts();
      setProducts(result.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTransactions = async () => {
    await refetchTransactions();
  };

  const fetchOutwardInvoices = async () => {
    try {
      const result = await returnService.getOutwardInvoicesForReturn();
      setOutwardInvoices(result.data || []);
    } catch (error) {
      console.error('Error fetching outward invoices:', error);
      toast.error('Failed to load outward invoices');
    }
  };

  // Get unique invoice numbers from outward invoices
  const getUniqueInvoices = () => {
    return outwardInvoices.map((inv) => ({
      invoiceNumber: inv.invoiceNumber,
      invoiceDate: inv.invoiceDate,
      vehicleNumber: inv.vehicleNumber,
      outwardTransactionId: inv.outwardTransactionId,
    }));
  };

  const resetForm = () => {
    setFormData({
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      productId: '',
      invoiceQuantity: 0,
      receivedQuantity: 0,
      returnReason: 'DEFECTIVE',
      vehicleNumber: '',
      location: '',
      batches: [],
    });
    setSelectedProducts([]);
    setBatchSelections({});
    // OLD auto-fill logic
    // setReceiveQtyMap({ batch1: product.dispatchedQty })
    // NEW: keep batch inputs empty initially
    setReceiveQtyMap({});
    setSelectedInvoiceData(null);
    setFormErrors({});
    setTouched({});
    setEditingTransactionId(null);
    setCurrentBatch({
      batchNumber: '',
      manufacturingDate: '',
      expiryDate: '',
      quantity: 0,
    });
  };

  // Wrapper for setFormData that includes validation
  const handleSetFormData = (updater) => {
    setFormData((prevFormData) => {
      const newFormData = typeof updater === 'function' ? updater(prevFormData) : updater;
      
      // Validate received quantity whenever it changes
      if (newFormData.receivedQuantity !== prevFormData.receivedQuantity) {
        setTouched((prev) => ({ ...prev, receivedQuantity: true }));
        
        const receivedQty = parseFloat(newFormData.receivedQuantity) || 0;
        const invoiceQty = newFormData.invoiceQuantity || 0;
        
        if (receivedQty < 0) {
          setFormErrors((prev) => ({
            ...prev,
            receivedQuantity: 'Received quantity cannot be negative',
          }));
        } else if (receivedQty > invoiceQty && invoiceQty > 0) {
          setFormErrors((prev) => ({
            ...prev,
            receivedQuantity: `Received quantity cannot exceed invoice quantity (${invoiceQty})`,
          }));
        } else {
          // Clear error if valid
          setFormErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.receivedQuantity;
            return newErrors;
          });
        }
        
        // OLD batch UI removed: batch validation now happens using `batchSelections` on submit.
        // (Keep legacy `batches` state intact for backend edit payloads / backwards-compat.)
      }
      
      return newFormData;
    });
  };

  const handleProductToggle = (productId, dispatchQuantity, batchDetails = []) => {
    setSelectedProducts((prev) => {
      const productIdStr = String(productId);
      const isSelected = prev.some((p) => String(p.productId) === productIdStr);
      
      if (isSelected) {
        // Remove product from selection - clear everything
        setFormData((prevForm) => ({
          ...prevForm,
          productId: '',
          invoiceQuantity: 0,
          receivedQuantity: 0,
        }));
        setBatchSelections((prevSel) => {
          const next = { ...prevSel };
          delete next[productIdStr];
          return next;
        });
        return [];
      } else {
        // Single select: replace previous selection with new one
        // Get batch details for the selected product
        const productItem = getInvoiceProducts().find(
          (item) => String(item.productId) === productIdStr
        );
        const batches = productItem?.batchDetails || batchDetails || [];
        
        const newInvoiceQty = dispatchQuantity;
        setFormData((prevForm) => {
          const updated = {
            ...prevForm,
            productId: productId,
            invoiceQuantity: newInvoiceQty,
            // Reset receivedQuantity when product changes
            receivedQuantity: 0,
          };
          
          // Clear any existing errors
          setFormErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.receivedQuantity;
            delete newErrors.batches;
            return newErrors;
          });
          
          return updated;
        });
        setBatchSelections((prevSel) => ({
          ...prevSel,
          [productIdStr]: prevSel[productIdStr] || {},
        }));
        // Return single item array (single select)
        return [{ productId, dispatchQuantity, batchDetails: batches, receivedQuantity: 0 }];
      }
    });
  };

  const handleBatchChange = (e) => {
    const { name, value } = e.target;
    setCurrentBatch({
      ...currentBatch,
      [name]: name === 'quantity' ? parseFloat(value) : value,
    });
  };

  const addBatch = () => {
    if (currentBatch.batchNumber && currentBatch.quantity > 0) {
      setFormData({
        ...formData,
        batches: [...formData.batches, { ...currentBatch }],
      });
      setCurrentBatch({
        batchNumber: '',
        manufacturingDate: '',
        expiryDate: '',
        quantity: 0,
      });
    }
  };

  const removeBatch = (index) => {
    setFormData({
      ...formData,
      batches: formData.batches.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (selectedProducts.length === 0) {
      toast.error('Please select a product');
      return;
    }
    
    // Backend expects a single product per return transaction; keep existing flow intact.
    if (selectedProducts.length > 1) {
      toast.error('Please select only one product');
      return;
    }

    const firstProduct = selectedProducts[0];
    const invoice = outwardInvoices.find(
      (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === formData.invoiceNumber
    );
    
    if (!invoice || !invoice.outwardTransactionId) {
      toast.error('Invalid invoice selection. Please select a valid invoice.');
      return;
    }

    const receivedQty = totalReceivedQtyNumber;

    if (receivedQty <= 0) {
      toast.error('Please enter received qty');
      return;
    }

    if (
      totalBatchQty <= 0 ||
      !hasValidBatchQty ||
      !Array.isArray(firstProduct?.batchDetails) ||
      firstProduct.batchDetails.length === 0
    ) {
      toast.error('Please enter batch qty');
      return;
    }

    // OLD - CORRECT
    if (totalBatchQty !== receivedQty) {
      toast.error("Total batch quantity must equal received quantity");
      return;
    }
    
    // Validate received quantity (product-level)
    const invoiceQty = firstProduct?.dispatchQuantity || formData.invoiceQuantity || 0;
    
    if (receivedQty < 0) {
      toast.error('Received quantity cannot be negative');
      return;
    }
    
    if (receivedQty > invoiceQty) {
      toast.error(`Received quantity (${receivedQty}) cannot exceed invoice quantity (${invoiceQty})`);
      return;
    }

    // OLD
    // if (totalReceivedQty > invoiceQty) {
    //   alert("Received qty cannot exceed dispatched qty");
    //   return;
    // }
    // NEW (safety)
    if (receivedQty > invoiceQty) {
      toast.error("Received qty cannot exceed dispatched qty");
      return;
    }
    
    // OLD WRONG VALIDATION
    // Validate batches (flat Excel-style)
    // for (const p of selectedProducts) {
    //   const pid = String(p.productId);
    //   const dispatchQty = Number(p.dispatchQuantity) || 0;
    //   const received = Number(totalReceivedQty) || 0;
    //   const batchTotal = Object.values(batchSelections[pid] || {}).reduce((a, b) => a + (Number(b) || 0), 0);
    //   if (batchTotal > dispatchQty || batchTotal !== received) {
    //     toast.error('Batch qty must match received qty');
    //     return;
    //   }
    // }

    // Build batches payload from invoice batchDetails + selected quantities
    const productIdStr = String(firstProduct.productId);
    const batchesPayload = (firstProduct.batchDetails || [])
      .map((b) => {
        const batchNumber = b.batchNumber;
        const qty = Number(batchSelections[productIdStr]?.[batchNumber]) || 0;
        return {
          batchNumber: batchNumber || '',
          manufacturingDate: b.manufacturingDate || b.mfgDate || b.mfg_date || undefined,
          expiryDate: b.expiryDate || b.exp_date || undefined,
          quantity: qty,
        };
      })
      .filter((b) => (Number(b.quantity) || 0) > 0);

    if (!batchesPayload.length || !batchesPayload.some((b) => Number(b.quantity || 0) > 0)) {
      toast.error('Please enter batch qty');
      return;
    }
    
    // Build dataToSubmit - ensure correct structure
    const dataToSubmit = {
      deliveryOrderNumber: formData.invoiceNumber,
      invoiceDate: formData.invoiceDate,
      productId: firstProduct.productId,
      invoiceQuantity: firstProduct.dispatchQuantity,
      receivedQuantity: receivedQty,
      returnReason: formData.returnReason,
      vehicleNumber: formData.vehicleNumber || '',
      location: formData.location?.trim() || undefined,
      outwardTransactionId: invoice.outwardTransactionId,
      batches: batchesPayload,
    };
    
    try {
      if (editingTransactionId) {
        // Update existing transaction
        await returnService.updateReturnTransaction(editingTransactionId, dataToSubmit);
        toast.success('Return transaction updated successfully');
      } else {
        // Create new transaction
        await returnService.createReturnTransaction(dataToSubmit);
        toast.success('Return transaction created successfully');
      }
      
      resetForm();
      setShowForm(false);
      fetchTransactions();
      window.dispatchEvent(new CustomEvent('putaway-refresh'));
    } catch (error) {
      console.error('Error saving return transaction:', error);
      toast.error(error?.message || 'Failed to save return transaction');
    }
  };

  const handleEdit = async (row) => {
    try {
      // Fetch full transaction details including batches
      const transactionDetails = await returnService.getReturnTransactionById(row._id || row.id);
      const transaction = transactionDetails.data || transactionDetails;
      
      // Prepare batchSelections from existing transaction batches
      const txBatchSelections = (transaction.batches || []).reduce((acc, b) => {
        const bn = (b.batchNumber || '').toString();
        if (!bn) return acc;
        acc[bn] = Number(b.quantity) || 0;
        return acc;
      }, {});
      
      // Find the invoice from outward invoices to get product details
      const invoice = outwardInvoices.find(
        (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === transaction.invoiceNumber
      );
      
      // Find product in invoice items
      let productItem = null;
      if (invoice && invoice.items) {
        productItem = invoice.items.find(
          (item) => String(item.productId) === String(transaction.productId)
        );
      }
      
      // Set selected product
      if (productItem) {
        setSelectedProducts([{
          productId: transaction.productId,
          dispatchQuantity: transaction.invoiceQuantity,
          batchDetails: productItem.batchDetails || [],
          receivedQuantity: transaction.receivedQuantity || 0,
        }]);
      }

      setBatchSelections((prev) => ({
        ...prev,
        [String(transaction.productId)]: txBatchSelections,
      }));
      
      // Populate form with existing transaction data
      setFormData({
        invoiceNumber: transaction.invoiceNumber || '',
        invoiceDate: transaction.invoiceDate 
          ? new Date(transaction.invoiceDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        productId: transaction.productId?._id || transaction.productId || '',
        invoiceQuantity: transaction.invoiceQuantity || 0,
        receivedQuantity: transaction.receivedQuantity || 0,
        returnReason: transaction.returnReason || 'DEFECTIVE',
        vehicleNumber: transaction.vehicleNumber || '',
        location: transaction.location || '',
        batches: transaction.batches || [],
      });
      
      // Set selected invoice data
      if (invoice) {
        setSelectedInvoiceData(invoice);
      }
      
      setEditingTransactionId(transaction._id || row._id || row.id);
      setShowForm(true);
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      toast.error('Failed to load transaction details');
    }
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeletePopUpOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      await returnService.deleteReturnTransaction(id);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      fetchTransactions();
      toast.success('Return transaction deleted successfully');
    } catch (err) {
      console.error('Error deleting return transaction:', err);
      toast.error(err?.message || 'Failed to delete return transaction');
    } finally {
      setDeleteLoading(false);
    }
  };

  const returnActions = [
    {
      label: 'Edit',
      icon: EditIcon,
      onClick: handleEdit,
    },
    {
      label: 'Delete',
      icon: DeleteIcon,
      onClick: handleDelete,
    },
  ];

  const openView = async (row) => {
    viewRowRef.current = row;
    setViewOpen(true);
    setViewLoading(true);
    setViewError(null);
    try {
      const res = await returnService.getReturnTransactionById(row?._id || row?.id);
      const transaction = res?.data || res;
      setViewData(transaction);
    } catch (err) {
      console.error('Error fetching return details:', err);
      setViewError(err?.message || 'Failed to load transaction details');
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };

  // Get products from selected invoice
  function getInvoiceProducts() {
    if (!selectedInvoiceData) return [];
    
    // Find the invoice from outwardInvoices
    const invoice = outwardInvoices.find(
      (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === selectedInvoiceData.invoiceNumber
    );
    
    if (!invoice || !invoice.items || invoice.items.length === 0) {
      console.log('No items found for invoice:', selectedInvoiceData.invoiceNumber);
      return [];
    }
    
    console.log('Invoice items:', invoice.items);
    
    // Return items with product details - ensure all products are included
    const productsList = invoice.items.map((item) => {
      // Handle product data - it might be populated object or just ID
      let product = item.product;
      const productId = item.productId;
      
      // If product is not populated, find it from products list
      if (!product || typeof product === 'string') {
        product = products.find((p) => p._id === productId || String(p._id) === String(productId));
      }
      
      return {
        productId: productId,
        product: product,
        dispatchQuantity: item.dispatchQuantity || 0,
        batchDetails: item.batchDetails || [],
      };
    }).filter((item) => item.productId); // Filter out any items without productId
    
    console.log('Processed products list:', productsList);
    return productsList;
  }

  const getReturnFields = () => [
    {
      name: 'invoiceNumber',
      label: 'DO Number',
      type: 'select',
      required: true,
      placeholder: 'Enter no',
      options: getUniqueInvoices().map((inv) => ({
        label: inv.deliveryOrderNumber || inv.invoiceNumber,
        value: inv.deliveryOrderNumber || inv.invoiceNumber,
      })),
      grid: '1',
    },
    {
      name: 'invoiceDate',
      label: 'Invoice Date',
      type: 'date',
      required: true,
      grid: '1',
      disabled: true,
    },
    {
      name: 'invoiceQuantity',
      label: 'Invoice Quantity',
      type: 'number',
      required: true,
      min: 1,
      grid: '1',
      disabled: true,
    },
    {
      name: 'returnReason',
      label: 'Return Reason',
      type: 'select',
      required: true,
      options: [
        { label: 'Defective', value: 'DEFECTIVE' },
        { label: 'Expired', value: 'EXPIRED' },
        { label: 'Quality Issue', value: 'QUALITY_ISSUE' },
        { label: 'Customer Return', value: 'CUSTOMER_RETURN' },
        { label: 'Other', value: 'OTHER' },
      ],
      grid: '1',
    },
  ];
  const handleExportReturnGRN = () => {
   // ✅ UI only for now (later backend connect)
   toast.info("GRN export will be added soon.");
 };
  return (
    <div
      className="page-container font-nunito"
      style={{ fontFamily: "'Nunito', sans-serif" }}
    >
      <div className="mb-6 w-full">
        <div
          className="rounded-[28px] border border-[#EEF1F5] bg-[#FFFFFF] px-6 py-4 shadow-sm"
          style={{ boxShadow: "0px 0px 4px 0px #959DA526" }}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      
      {/* Left */}
      <div className="min-w-0">
        <h1
          style={{
            fontFamily: "Nunito",
            fontSize: 18,
            fontWeight: 600,
            color: "#161616",
            margin: 0,
          }}
          className="whitespace-nowrap"
        >
          Return Management
        </h1>
      </div>

      {/* Right */}
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center md:w-auto">
        
        {/* ✅ EXACT same search as Company */}
        <div className="w-full sm:w-[320px]">
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </span>

            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by ID"
              className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-700 placeholder:text-gray-400 shadow-sm focus:border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            />
        </div>
        </div>

        {/* ✅ EXACT same button style */}
        <button
  type="button"
  onClick={() => {
    resetForm();
    setShowForm(true);
  }}
  className="h-11 shrink-0 rounded-xl bg-[#188742] hover:bg-[#146c36] px-6 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98]"
>
  Create Return Transaction
</button>
      </div>
    </div>
  </div>
</div>



      <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => { setDeletePopUpOpen(false); setRowToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        entityLabel="return transaction"
      />
      <FormPopup
        open={showForm}
        title={editingTransactionId ? "Edit Return Transaction" : "Create New Return"}
        submitText={editingTransactionId ? "Update" : "Create Return"}
        onClose={() => {
          resetForm();
          setShowForm(false);
        }}
        onSubmit={handleSubmit}
        submitDisabled={loading}
        values={formData}
        setValues={handleSetFormData}
        fields={getReturnFields()}
        errors={formErrors}
        touched={touched}
        width="xl"
        bodyGridColumns={4}
      >
        {selectedInvoiceData && (
          <div className="mt-6 rounded-3xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Products</h3>
            {/* Product selection list (kept so flow doesn't break) */}
            <div className="space-y-2">
              {getInvoiceProducts().map((item, index) => {
                const isSelected = selectedProducts.some((p) => String(p.productId) === String(item.productId));
                const product = item.product;
                const productKey = item.productId || `product-${index}`;
                return (
                  <label
                    key={productKey}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleProductToggle(item.productId, item.dispatchQuantity, item.batchDetails)}
                      className="w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {product?.sku || item.productId || 'N/A'} - {product?.skuName || 'Product not found'}
                      </div>
                      <div className="text-xs text-gray-500">
                        Dispatched: <span className="font-medium">{item.dispatchQuantity}</span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* OLD nested batch UI removed */}

            {/* OLD UI */}
            {/* Rendering product info inside batch loop (causing repetition) */}
            {false && selectedFlatRows.length > 0 && (
              <div className="mt-4">
                <div className="grid grid-cols-9 gap-4 items-center border-b border-gray-200 pb-2 text-sm font-semibold text-gray-900">
                  <span>Select</span>
                  <span>Product</span>
                  <span>Dispatched Qty</span>
                  <span>Received Qty</span>
                  <span>Invoice Qty</span>
                  <span>Batch</span>
                  <span>Mfg</span>
                  <span>Exp</span>
                  <span>Qty</span>
                </div>

                {selectedFlatRows.map((row, idx) => {
                  const productIdStr = String(row.productId);
                  const isSelected = selectedProducts.some((p) => String(p.productId) === productIdStr);
                  return (
                    <div key={idx} className="grid grid-cols-9 gap-4 items-center py-2 border-b">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          if (isSelected) {
                            handleProductToggle(row.productId, row.dispatchQuantity, []);
                          }
                        }}
                        className="w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                      />

                      <span className="text-sm text-gray-900">{row.productName}</span>

                      <span className="text-sm text-gray-700">{row.dispatchQuantity}</span>

                      <input
                        type="number"
                        value={row.receivedQuantity}
                        className="w-16 border rounded px-1"
                        min="0"
                        max={row.dispatchQuantity}
                        onChange={(e) => {
                          const val = Number(e.target.value) || 0;
                          setSelectedProducts((prev) =>
                            prev.map((p) =>
                              String(p.productId) === productIdStr ? { ...p, receivedQuantity: val } : p
                            )
                          );
                          handleSetFormData((prev) => ({ ...prev, receivedQuantity: val }));
                        }}
                      />

                      <span className="text-sm text-gray-700">{row.invoiceQuantity}</span>

                      <span className="text-sm text-gray-700">{row.batchNumber}</span>

                      <span className="text-sm text-gray-700">
                        {row.mfgDate ? new Date(row.mfgDate).toLocaleDateString() : '-'}
                      </span>

                      <span className="text-sm text-gray-700">
                        {row.expiryDate ? new Date(row.expiryDate).toLocaleDateString() : '-'}
                      </span>

                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          max={row.availableQuantity}
                          className="w-14 border rounded px-1"
                          value={batchSelections[productIdStr]?.[row.batchNumber] || ''}
                          onChange={(e) => {
                            const raw = Number(e.target.value) || 0;
                            const maxQty = Number(row.availableQuantity) || 0;
                            const val = Math.min(Math.max(0, raw), maxQty);
                            setBatchSelections((prev) => ({
                              ...prev,
                              [productIdStr]: {
                                ...(prev[productIdStr] || {}),
                                [row.batchNumber]: val,
                              },
                            }));
                          }}
                        />
                        <span className="text-gray-500 text-xs">/ {row.availableQuantity}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* NEW UI */}
            {/* Show product details once, batches in a table underneath */}
            {selectedProducts.length > 0 && (
              <div className="mt-4 space-y-4">
                {selectedProducts.map((p) => {
                  const pid = String(p.productId);
                  const invoiceItem = getInvoiceProducts().find((it) => String(it.productId) === pid);
                  const productObj = invoiceItem?.product || null;
                  const sku = productObj?.sku || pid;
                  const name = productObj?.description || '';
                  const dispatchedQty = Number(p.dispatchQuantity || 0);
                  const batches = Array.isArray(p.batchDetails) ? p.batchDetails : [];

                  return (
                    <div
                      key={pid}
                      className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-sm font-semibold text-gray-900">
                          {sku} {name ? `- ${name}` : ''}
                        </div>
                        <div className="text-sm text-gray-700">
                          Dispatched: <span className="font-semibold">{dispatchedQty}</span>
                        </div>
                        <div className="text-sm text-gray-700">
                          Received:{' '}
                          <input
                            type="number"
                            min="0"
                            max={dispatchedQty}
                            className="ml-2 w-24 border rounded px-2 py-1 text-sm"
                            value={totalReceivedQty}
                            onChange={(e) => setTotalReceivedQty(e.target.value)}
                          />
                          <span className="ml-2 text-gray-500">/ {dispatchedQty}</span>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="grid grid-cols-5 gap-3 border-b border-gray-200 pb-2 text-xs font-semibold text-gray-700">
                          <span>Batch</span>
                          <span>Mfg</span>
                          <span>Exp</span>
                          <span className="text-right">Qty</span>
                          <span className="text-right">Receive</span>
                        </div>

                        <div className="max-h-[280px] overflow-auto">
                          {batches.length > 0 ? (
                            batches.map((b, idx) => {
                              const batchNumber = b.batchNumber || `BATCH-${idx + 1}`;
                              const mfg = b.mfgDate ?? b.manufacturingDate ?? null;
                              const exp = b.expiryDate ?? null;
                              const avail = Number(b.availableQuantity ?? b.quantity ?? 0);
                              const batchKey = String(b.id || b._id || b.batchId || `${batchNumber}${idx}`);
                              // OLD
                              // const value = receiveQtyMap[batchKey] ?? batchSelections[pid]?.[batchNumber] ?? '';
                              // NEW
                              const value = receiveQtyMap[batchKey] || '';

                              return (
                                <div
                                  key={b.id || b._id || b.batchId || `${batchNumber}${idx}`}
                                  className="grid grid-cols-5 gap-3 items-center py-2 border-b border-gray-100 text-sm"
                                >
                                  <span className="text-gray-900">{batchNumber}</span>
                                  <span className="text-gray-700">
                                    {mfg ? new Date(mfg).toLocaleDateString() : '-'}
                                  </span>
                                  <span className="text-gray-700">
                                    {exp ? new Date(exp).toLocaleDateString() : '-'}
                                  </span>
                                  <span className="text-right text-gray-700">{avail}</span>
                                  <div className="flex justify-end items-center gap-2">
                                    <input
                                      type="number"
                                      min="0"
                                      max={avail}
                                      className="w-20 border rounded px-2 py-1 text-sm"
                                      // OLD
                                      // value={receiveQty}
                                      // onChange={(e) => setReceiveQty(e.target.value)}
                                      // NEW
                                      value={value}
                                      onChange={(e) => {
                                        const next = e.target.value;
                                        setReceiveQtyMap((prev) => ({
                                          ...prev,
                                          [batchKey]: next,
                                        }));

                                        const raw = Number(next) || 0;
                                        const val = Math.min(Math.max(0, raw), avail);
                                        setBatchSelections((prev) => ({
                                          ...prev,
                                          [pid]: {
                                            ...(prev[pid] || {}),
                                            [batchNumber]: val,
                                          },
                                        }));
                                      }}
                                    />
                                    <span className="text-gray-400 text-xs">/ {avail}</span>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="py-3 text-sm text-gray-500">No batches found for this product.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {getInvoiceProducts().length === 0 && selectedInvoiceData && (
              <div className="text-sm text-gray-500 text-center py-4">
                <p>No products found for invoice: {selectedInvoiceData.invoiceNumber}</p>
                <p className="text-xs mt-2">Please check if the invoice has items in outward management.</p>
              </div>
            )}
            {isReturnQtyInvalid && selectedProducts.length > 0 && (
              <div className="mt-3 text-sm text-red-600">
                Please enter received qty and batch qty
              </div>
            )}
          </div>
        )}
        {/* OLD batch UI removed */}
      </FormPopup>
      <ReturnDetailsModal
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewData(null);
          setViewError(null);
        }}
        loading={viewLoading}
        error={viewError}
        data={viewData}
        onRetry={() => viewRowRef.current && openView(viewRowRef.current)}
      />
      <div className="mb-3 flex items-center justify-end gap-2">
</div>
      {loading ? (
        <SkeletonTable cols={returnColumns.length + 1} rows={8} />
      ) : (
        <Table
          columns={returnColumns}
          data={filteredTransactions}
          onRowClick={(row) => openView(row)}
          loading={false}
          emptyMessage="No return transactions found"
          actions={returnActions}
        />
      )}
    </div>
  );
}
